// src/pages/reports/FinancialReports.js

import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";

export default function FinancialReports() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";

  const [financials, setFinancials] = useState({
    totalRevenue: 0, totalExpenses: 0, totalPurchases: 0,
    vendorOutstanding: 0, netProfit: 0, pendingInvoices: 0,
  });
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => { if (companyId) fetchFinancialReports(); }, [companyId]);

  const fetchFinancialReports = async () => {
    try {
      setLoading(true);
      let totalRevenue = 0, totalExpenses = 0, totalPurchases = 0;
      let vendorOutstanding = 0, pendingInvoices = 0;
      const monthlyMap = {};

      const addToMonth = (ts, key, amount) => {
        const seconds = ts?.seconds;
        if (!seconds) return;
        const d   = new Date(seconds * 1000);
        const mk  = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        if (!monthlyMap[mk]) monthlyMap[mk] = { revenue: 0, expense: 0 };
        monthlyMap[mk][key] += amount;
      };

      // ── Invoices ──────────────────────────────────────
      const invSnap = await getDocs(
        query(collection(db, "invoices"), where("company_id", "==", companyId))
      );
      invSnap.forEach(d => {
        const data   = d.data();
        const amount = Number(data.total_amount   || 0);
        const bal    = Number(data.balance_amount || 0);
        totalRevenue += amount;
        if (data.payment_status !== "Paid") pendingInvoices += bal;
        addToMonth(data.invoice_date, "revenue", amount);
      });

      // ── Expenses + Transport ──────────────────────────
      const [expSnap, transSnap] = await Promise.all([
        getDocs(query(collection(db, "financial_transactions"),
          where("company_id", "==", companyId), where("type", "==", "expense"))),
        getDocs(query(collection(db, "financial_transactions"),
          where("company_id", "==", companyId), where("type", "==", "transport"))),
      ]);
      [...expSnap.docs, ...transSnap.docs].forEach(d => {
        const data = d.data();
        const amt  = Number(data.amount || 0);
        totalExpenses += amt;
        addToMonth(data.created_at, "expense", amt);
      });

      // ── Purchase Orders ───────────────────────────────
      const poSnap = await getDocs(
        query(collection(db, "purchase_orders"), where("company_id", "==", companyId))
      );
      poSnap.forEach(d => {
        const data = d.data();
        const amt  = Number(data.grand_total || data.total_amount || 0);
        totalPurchases += amt;
        if (data.po_status !== "paid" && data.balance_amount)
          vendorOutstanding += Number(data.balance_amount || 0);
        addToMonth(data.created_at, "expense", amt);
      });

      const monthlyDataArray = Object.entries(monthlyMap)
        .map(([month, v]) => ({
          month, revenue: v.revenue, expense: v.expense,
          profit: v.revenue - v.expense,
        }))
        .sort((a, b) => a.month.localeCompare(b.month));

      setFinancials({
        totalRevenue, totalExpenses, totalPurchases,
        vendorOutstanding, pendingInvoices,
        netProfit: totalRevenue - totalExpenses - totalPurchases,
      });
      setMonthlyData(monthlyDataArray);
    } catch (err) {
      console.error("Financial reports error:", err);
    } finally { setLoading(false); }
  };

  const f = financials;

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Financial Reports</h1>

      {/* SUMMARY */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard title="Total Revenue"       value={`₹${f.totalRevenue.toLocaleString()}`}       color="green" />
        <StatCard title="Expenses"            value={`₹${f.totalExpenses.toLocaleString()}`}       color="red" />
        <StatCard title="Purchases"           value={`₹${f.totalPurchases.toLocaleString()}`}      color="orange" />
        <StatCard title="Vendor Outstanding"  value={`₹${f.vendorOutstanding.toLocaleString()}`}   color="amber" />
        <StatCard title="Pending Collection"  value={`₹${f.pendingInvoices.toLocaleString()}`}     color="amber" />
        <StatCard title="Net Profit"
          value={`₹${f.netProfit.toLocaleString()}`}
          color={f.netProfit >= 0 ? "green" : "red"} />
      </div>

      {/* MONTHLY TABLE */}
      <div className="bg-white rounded-xl shadow p-4">
        <h2 className="text-lg font-semibold mb-4">Monthly Financial Summary</h2>
        {loading ? (
          <p className="text-slate-400 text-sm">Loading...</p>
        ) : monthlyData.length === 0 ? (
          <p className="text-slate-400 text-sm">No financial data available.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="p-3 text-left">Month</th>
                  <th className="p-3 text-right">Revenue</th>
                  <th className="p-3 text-right">Expenses</th>
                  <th className="p-3 text-right">Profit</th>
                  <th className="p-3 text-left">Margin</th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.map((item, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 font-medium">{item.month}</td>
                    <td className="p-3 text-right text-green-600">₹{item.revenue.toLocaleString()}</td>
                    <td className="p-3 text-right text-red-600">₹{item.expense.toLocaleString()}</td>
                    <td className={`p-3 text-right font-semibold ${item.profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                      ₹{item.profit.toLocaleString()}
                    </td>
                    <td className="p-3 text-slate-500 text-xs">
                      {item.revenue > 0
                        ? `${((item.profit / item.revenue) * 100).toFixed(1)}%`
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* INSIGHTS */}
      <div className="bg-white rounded-xl shadow p-4">
        <h2 className="text-lg font-semibold mb-3">Business Insights</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">Revenue Efficiency</span>
            <span className="font-semibold">
              {f.totalRevenue > 0
                ? `${((f.netProfit / f.totalRevenue) * 100).toFixed(2)}%`
                : "-"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Outstanding Risk</span>
            <span className="font-semibold text-red-600">
              ₹{(f.vendorOutstanding + f.pendingInvoices).toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Expense Ratio</span>
            <span className="font-semibold">
              {f.totalRevenue > 0
                ? `${(((f.totalExpenses + f.totalPurchases) / f.totalRevenue) * 100).toFixed(1)}%`
                : "-"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, color = "blue" }) {
  const colors = {
    green:  "bg-green-50  border-green-200",
    red:    "bg-red-50    border-red-200",
    orange: "bg-orange-50 border-orange-200",
    amber:  "bg-amber-50  border-amber-200",
    blue:   "bg-blue-50   border-blue-200",
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color] || colors.blue}`}>
      <p className="text-xs text-slate-500 uppercase tracking-wide">{title}</p>
      <h3 className="text-xl font-bold mt-1">{value}</h3>
    </div>
  );
}
